import { defineSlotRecipe } from '@chakra-ui/react'
import * as controls from '@src/theme/next/controls.ts'
import { alertSlotRecipe } from '@src/theme/slot-recipes/alert'
import { cardSlotRecipe } from '@src/theme/slot-recipes/card'
import { checkboxSlotRecipe } from '@src/theme/slot-recipes/checkbox'
import { comboboxSlotRecipe } from '@src/theme/slot-recipes/combobox'
import { dialogSlotRecipe } from '@src/theme/slot-recipes/dialog'
import { drawerSlotRecipe } from '@src/theme/slot-recipes/drawer'
import { fieldSlotRecipe } from '@src/theme/slot-recipes/field'
import { hoverCardSlotRecipe } from '@src/theme/slot-recipes/hover-card'
import { menuSlotRecipe } from '@src/theme/slot-recipes/menu'
import { nativeSelectSlotRecipe } from '@src/theme/slot-recipes/native-select'
import { numberInputSlotRecipe } from '@src/theme/slot-recipes/number-input'
import { popoverSlotRecipe } from '@src/theme/slot-recipes/popover'
import { progressSlotRecipe } from '@src/theme/slot-recipes/progress'
import { radioGroupSlotRecipe } from '@src/theme/slot-recipes/radio-group'
import { segmentGroupSlotRecipe } from '@src/theme/slot-recipes/segment-group'
import { selectSlotRecipe } from '@src/theme/slot-recipes/select'
import { stepsSlotRecipe } from '@src/theme/slot-recipes/steps'
import { switchSlotRecipe } from '@src/theme/slot-recipes/switch'
import { tableSlotRecipe } from '@src/theme/slot-recipes/table'
import { tabsSlotRecipe } from '@src/theme/slot-recipes/tabs'
import { tagSlotRecipe } from '@src/theme/slot-recipes/tag'
import { timelineSlotRecipe } from '@src/theme/slot-recipes/timeline'

// Slot recipe overrides, keyed like src/theme/slot-recipes (card: defineSlotRecipe({...}), …).
// Overrides deep-merge into the production recipe, so only the keys set here change. Each one
// reuses the production slot list so no slot loses its styles in the merge.

// Raised surface edge: long soft drop (the LP's frames) plus a 1px top highlight in dark.
const raised = {
  _light: '0px 30px 60px -28px {colors.gray.900/22}, 0px 0px 1px {colors.gray.900/20}',
  _dark: '0px 30px 80px -40px {colors.black/60}, inset 0px 1px 0px {colors.whiteAlpha.100}',
}

// The LP's SegmentedTabs active pill: a lifted chip with a hairline ring (inset, so no layout shift).
const activePill = {
  bg: { _light: 'bg.panel', _dark: 'bg.muted' },
  color: 'fg',
  shadow: 'inset 0 0 0 1px {colors.border.emphasized}',
}

// Row hover and stripes: a faint neutral wash, so it reads on translucent cards.
const rowWash = { _light: 'blackAlpha.50', _dark: 'whiteAlpha.50' }

// Floating surfaces share one fill and one edge (bg.overlay, shadows.overlay).
const floating = { bg: 'bg.overlay', boxShadow: 'overlay' }

// Highlighted list items: a neutral wash, not a palette fill.
const itemWash = { _light: 'blackAlpha.50', _dark: 'whiteAlpha.100' }

// Modal scrim: darker, with a light blur so the page recedes instead of just dimming.
const scrim = { bg: 'blackAlpha.600', backdropFilter: 'blur(4px)' }

// The mockups' readout callout (.w-read): a hairline box with a 3px accent edge on the start side
// and a tint that fades out by the middle. Text stays neutral; only the edge and icon carry color.
const callout = {
  root: {
    bg: 'transparent',
    bgImage: {
      _light: 'linear-gradient(90deg, {colors.colorPalette.500/8}, transparent 45%)',
      _dark: 'linear-gradient(90deg, {colors.colorPalette.400/10}, transparent 45%)',
    },
    color: 'fg',
    borderWidth: '1px',
    borderColor: 'border',
    borderStartWidth: '3px',
    borderStartColor: { _light: 'colorPalette.500', _dark: 'colorPalette.400' },
    borderStartRadius: 'md',
    borderEndRadius: 'xl',
    shadow: 'none',
  },
  description: { color: 'fg.muted' },
  indicator: { color: { _light: 'colorPalette.600', _dark: 'colorPalette.400' } },
}

// The mockups' step node at rest (.node): punched out of the card in the page color, with a
// 2px neutral ring and a subtle number.
const lockedNode = {
  bg: 'bg',
  borderWidth: '2px',
  borderColor: 'border.emphasized',
  color: 'fg.subtle',
}

export const slotRecipes = {
  // The mockups' step card: a faint fill over the page with a neutral hairline and big corners.
  // Edges come from lines, not shadows; elevated is the one raised treatment.
  card: defineSlotRecipe({
    slots: cardSlotRecipe.slots,
    variants: {
      size: {
        sm: { root: { borderRadius: 'xl' } },
        md: { root: { borderRadius: '2xl' } },
        lg: { root: { borderRadius: '3xl' } },
      },
      variant: {
        // Default card.
        outline: {
          root: { bg: 'colorPalette.panel', borderColor: 'border' },
        },
        // Featured or active card: firmer edge, deeper fill, long shadow.
        elevated: {
          root: {
            // colorPalette.card is orange.50 in light, which reads sunken on white; stay white.
            bg: { _light: 'colorPalette.panel', _dark: 'colorPalette.card' },
            borderWidth: '1px',
            borderColor: 'border.emphasized',
            boxShadow: raised,
          },
        },
        // Nested inside another card: its own hairline so it reads against the parent.
        subtle: {
          root: {
            bg: 'colorPalette.container',
            borderWidth: '1px',
            borderColor: 'border',
            boxShadow: 'none',
          },
        },
        // Recessed inset, like the mockups' preview wells: the page color showing through.
        muted: {
          root: {
            bg: 'bg',
            borderWidth: '1px',
            borderColor: 'border.muted',
            boxShadow: 'none',
          },
        },
        // Linked cards: the default look, then the edge firms up and the card lifts. No scale.
        interactive: {
          root: {
            bg: 'colorPalette.panel',
            borderColor: 'border',
            _hover: {
              bg: 'colorPalette.card',
              borderColor: 'border.emphasized',
              transform: 'none',
              boxShadow: raised,
            },
          },
        },
      },
    },
  }),

  // Quiet labels so the value carries the field (the mockups' labels sit in the subtle tone).
  field: defineSlotRecipe({
    slots: fieldSlotRecipe.slots,
    base: {
      label: { color: 'fg.muted' },
      helperText: { color: 'fg.subtle' },
    },
  }),

  // The rest share the input's well and line (./controls.ts).
  nativeSelect: defineSlotRecipe({
    slots: nativeSelectSlotRecipe.slots,
    base: { field: controls.text },
    variants: {
      variant: {
        subtle: { field: controls.variants.subtle },
        outline: { field: controls.variants.outline },
      },
    },
  }),

  select: defineSlotRecipe({
    slots: selectSlotRecipe.slots,
    base: {
      trigger: controls.text,
      // The list is an overlay (see menu below).
      content: { ...floating, borderRadius: 'xl' },
      item: { borderRadius: 'lg', _highlighted: { bg: itemWash } },
    },
    variants: {
      variant: {
        subtle: { trigger: controls.variants.subtle },
        outline: { trigger: controls.variants.outline },
      },
    },
  }),

  combobox: defineSlotRecipe({
    slots: comboboxSlotRecipe.slots,
    base: {
      input: controls.text,
      content: { ...floating, borderRadius: 'xl' },
      item: { borderRadius: 'lg', _highlighted: { bg: itemWash } },
    },
    variants: {
      variant: {
        subtle: { input: controls.variants.subtle },
        outline: { input: controls.variants.outline },
        flushed: { input: controls.variants.flushed },
      },
    },
  }),

  numberInput: defineSlotRecipe({
    slots: numberInputSlotRecipe.slots,
    base: { input: controls.text },
    variants: {
      variant: {
        subtle: { input: controls.variants.subtle },
        outline: { input: controls.variants.outline },
        flushed: { input: controls.variants.flushed },
      },
    },
  }),

  // Same pill as the badge: rounded, tinted, hairline ring (the mockups' feature tags).
  tag: defineSlotRecipe({
    slots: tagSlotRecipe.slots,
    base: { root: { borderRadius: 'full' } },
    variants: {
      size: {
        sm: { root: { px: '2' } },
        md: { root: { px: '2.5', minH: '6' } },
        lg: { root: { px: '3', minH: '7' } },
        xl: { root: { px: '3.5' } },
      },
      variant: {
        surface: { root: controls.tint },
        subtle: { root: { ...controls.tint, shadow: 'none' } },
      },
    },
  }),

  // Tabs follow the LP's SegmentedTabs: muted labels that brighten on hover, a rounded active
  // pill, neutral hairlines instead of palette borders.
  tabs: defineSlotRecipe({
    slots: tabsSlotRecipe.slots,
    base: {
      trigger: {
        transitionProperty: 'color, background, box-shadow',
        transitionDuration: 'moderate',
        _hover: { color: 'fg' },
      },
    },
    variants: {
      variant: {
        line: {
          list: { borderColor: 'border' },
        },
        // The LP's segmented track: a rounded, blurred bar with the active pill inside.
        enclosed: {
          list: {
            bg: 'bg.subtle/80',
            backdropFilter: 'blur(16px)',
            borderWidth: '1px',
            borderColor: 'border.emphasized',
            borderRadius: 'full',
          },
          trigger: {
            borderRadius: 'full',
            _selected: activePill,
          },
        },
        // The active pill without the track.
        subtle: {
          trigger: {
            borderRadius: 'full',
            _selected: activePill,
          },
        },
        outline: {
          list: { borderColor: 'border' },
          trigger: {
            _selected: { color: 'fg' },
            _horizontal: { _selected: { borderColor: 'border', borderBottomColor: 'transparent' } },
            _vertical: { _selected: { borderColor: 'border', borderEndColor: 'transparent' } },
          },
        },
      },
    },
  }),

  // The mockups' readouts and row lists: no fill, quiet column labels, hairline row dividers a
  // step lighter than the header rule.
  table: defineSlotRecipe({
    slots: tableSlotRecipe.slots,
    base: {
      columnHeader: { color: 'fg.muted' },
    },
    variants: {
      interactive: {
        true: { body: { '& tr': { _hover: { bg: rowWash } } } },
      },
      striped: {
        true: { row: { '&:nth-of-type(odd) td': { bg: rowWash } } },
      },
      variant: {
        line: {
          row: { bg: 'transparent' },
          columnHeader: { borderColor: 'border' },
          cell: { borderColor: 'border.muted' },
        },
        outline: {
          row: { borderColor: 'border.muted' },
          columnHeader: { borderColor: 'border' },
          footer: { borderColor: 'border' },
        },
      },
    },
  }),

  // Overlays: bg.overlay + hairline ring + long drop, rounder corners, neutral item highlights.
  menu: defineSlotRecipe({
    slots: menuSlotRecipe.slots,
    base: {
      content: { ...floating, borderRadius: 'xl' },
      item: { borderRadius: 'lg' },
      separator: { bg: 'border' },
    },
    variants: {
      variant: {
        subtle: { item: { _highlighted: { bg: itemWash } } },
      },
    },
  }),

  popover: defineSlotRecipe({
    slots: popoverSlotRecipe.slots,
    base: {
      content: { '--popover-bg': 'colors.bg.overlay', boxShadow: 'overlay', borderRadius: 'xl' },
    },
  }),

  hoverCard: defineSlotRecipe({
    slots: hoverCardSlotRecipe.slots,
    base: {
      content: {
        '--hovercard-bg': 'colors.bg.overlay',
        borderColor: 'transparent',
        boxShadow: 'overlay',
        borderRadius: 'xl',
      },
    },
  }),

  dialog: defineSlotRecipe({
    slots: dialogSlotRecipe.slots,
    base: {
      backdrop: scrim,
      content: { ...floating, borderRadius: '2xl' },
    },
  }),

  drawer: defineSlotRecipe({
    slots: drawerSlotRecipe.slots,
    base: {
      backdrop: scrim,
      content: floating,
    },
  }),

  alert: defineSlotRecipe({
    slots: alertSlotRecipe.slots,
    variants: {
      variant: {
        subtle: callout,
        surface: callout,
        outline: { ...callout, root: { ...callout.root, bgImage: 'none' } },
      },
    },
  }),

  // The mockups' pool bar: a thin pill track in a raised neutral, with a flat palette fill.
  progress: defineSlotRecipe({
    slots: progressSlotRecipe.slots,
    variants: {
      variant: {
        outline: {
          track: { shadow: 'none', bgColor: { _light: 'blackAlpha.100', _dark: 'bg.emphasized' } },
        },
      },
      shape: {
        rounded: { track: { borderRadius: 'full' }, range: { borderRadius: 'full' } },
      },
      size: {
        md: { track: { h: '1.5' } },
      },
    },
  }),

  // Checkbox and radio: unchecked boxes sit in the same recessed well as fields; checked fills
  // flat with the palette solid, like solid buttons.
  checkbox: defineSlotRecipe({
    slots: checkboxSlotRecipe.slots,
    variants: {
      variant: {
        solid: {
          control: {
            // Only while unchecked: a conditional bg would outrank production's checked fill.
            '&:not([data-state=checked]):not([data-state=indeterminate])': { bg: controls.well },
            _hover: { borderColor: 'fg.subtle' },
          },
        },
      },
    },
  }),

  radioGroup: defineSlotRecipe({
    slots: radioGroupSlotRecipe.slots,
    variants: {
      variant: {
        solid: {
          itemControl: {
            '&:not([data-state=checked])': { bg: controls.well },
            _hover: { borderColor: 'fg.subtle' },
          },
        },
      },
    },
  }),

  // Segmented control matches enclosed tabs (the LP's SegmentedTabs): a rounded track with a
  // hairline, no dividers, muted labels, and the active pill sliding underneath.
  segmentGroup: defineSlotRecipe({
    slots: segmentGroupSlotRecipe.slots,
    base: {
      root: {
        '--segment-radius': 'radii.full',
        '--segment-indicator-bg': { _light: 'colors.bg.panel', _dark: 'colors.bg.muted' },
        '--segment-indicator-shadow': 'inset 0 0 0 1px {colors.border.emphasized}',
        bg: 'bg.subtle/80',
        boxShadow: 'inset 0 0 0 1px {colors.border.emphasized}',
      },
      item: {
        color: 'fg.muted',
        transition: 'color 0.2s',
        _hover: { color: 'fg' },
        _checked: { color: 'fg' },
        _before: { display: 'none' },
      },
    },
  }),

  // The thumb stays white when on. Production uses colorPalette.contrast, which is near-black for
  // orange here (see semantic-tokens), so the light-mode thumb went dark.
  switch: defineSlotRecipe({
    slots: switchSlotRecipe.slots,
    variants: {
      variant: {
        solid: { thumb: { _checked: { bg: 'white' } } },
      },
    },
  }),

  // Step nodes from the game plan timeline: locked (ring), active (palette ring + halo), done
  // (solid fill). The rail is a rounded 2px line that fills as steps complete.
  steps: defineSlotRecipe({
    slots: stepsSlotRecipe.slots,
    base: {
      indicator: { fontWeight: 'semibold' },
      separator: { bg: 'border.emphasized', borderRadius: 'full' },
    },
    variants: {
      variant: {
        solid: {
          indicator: {
            _incomplete: lockedNode,
            _current: {
              bg: 'bg',
              borderColor: 'colorPalette.solid',
              color: { _light: 'colorPalette.600', _dark: 'colorPalette.400' },
              boxShadow: '0 0 0 5px {colors.colorPalette.solid/16}',
            },
          },
        },
      },
    },
  }),

  // Timeline has no states; outline is the locked node, and the rail matches steps.
  timeline: defineSlotRecipe({
    slots: timelineSlotRecipe.slots,
    base: {
      root: { '--timeline-thickness': '2px' },
      separator: { borderColor: 'border.emphasized' },
      indicator: { fontWeight: 'semibold' },
    },
    variants: {
      variant: {
        outline: { indicator: lockedNode },
      },
      // Bigger nodes at the large sizes; the mockups' step nodes are 28-34px.
      size: {
        lg: { root: { '--timeline-indicator-size': 'sizes.10', '--timeline-font-size': 'fontSizes.sm' } },
        xl: { root: { '--timeline-indicator-size': 'sizes.12', '--timeline-font-size': 'fontSizes.md' } },
      },
    },
  }),
}
